import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', due_date: '', priority: 'medium', status: 'todo' });

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      const { data } = await axios.get(`${API}/tasks`, { withCredentials: true });
      setTasks(data);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/tasks`, formData, { withCredentials: true });
      toast.success('Task created');
      setShowDialog(false);
      setFormData({ title: '', description: '', due_date: '', priority: 'medium', status: 'todo' });
      loadTasks();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { status: newStatus }, { withCredentials: true });
      toast.success('Task updated');
      loadTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const priorityColors = {
    low: 'bg-blue-100 text-blue-800 border-blue-200',
    medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    high: 'bg-red-100 text-red-800 border-red-200'
  };

  const statusColors = {
    todo: 'bg-zinc-100 text-zinc-800 border-zinc-200',
    in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
    completed: 'bg-green-100 text-green-800 border-green-200'
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8" data-testid="tasks-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}} data-testid="tasks-title">
            Tasks
          </h1>
          <p className="mt-2 text-zinc-600">Manage your team tasks</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="bg-black text-white hover:bg-zinc-800 gap-2" data-testid="add-task-button">
              <Plus size={18} /> Add Task
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Add Task</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  data-testid="task-title-input"
                  className="mt-2 border-zinc-300 hover:border-black transition-colors"
                />
              </div>
              <div>
                <Label htmlFor="description" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  data-testid="task-description-input"
                  className="mt-2 border-zinc-300 hover:border-black transition-colors"
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="priority" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Priority</Label>
                <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                  <SelectTrigger className="mt-2" data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="due_date" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Due Date</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                  data-testid="task-due-date-input"
                  className="mt-2 border-zinc-300 hover:border-black transition-colors"
                />
              </div>
              <Button type="submit" className="w-full bg-black text-white hover:bg-zinc-800" data-testid="submit-task-button">
                Create Task
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-zinc-200">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="tasks-table">
            <thead className="border-b border-zinc-200 bg-zinc-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Title</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Priority</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Status</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Due Date</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {tasks.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-zinc-500" data-testid="no-tasks">
                    No tasks yet. Add your first task to get started.
                  </td>
                </tr>
              ) : (
                tasks.map((task) => (
                  <tr key={task.id} className="hover:bg-zinc-50 transition-colors" data-testid={`task-row-${task.id}`}>
                    <td className="px-6 py-4 font-medium text-zinc-900">{task.title}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold border ${priorityColors[task.priority]}`}>
                        {task.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <Select value={task.status} onValueChange={(value) => updateTaskStatus(task.id, value)}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todo">To Do</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-6 py-4 text-zinc-600 text-sm">
                      {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-zinc-600 text-sm">
                      {task.description || 'No description'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
